<script lang="ts">
    import { _ } from "svelte-i18n"
    import { addItemToCart, cart } from "../../../../functionTools/cart"
    import { getProductPrice, deepEqual } from "../../../../utils"
    import { login } from "../../../../functionTools/login"
    import { lasersheetEngraving, overlay } from "../../../../store"
    import QuantityCounter from "./QuantityCounter.svelte"
    import { isLasersheetConfigValid } from "../widgets/helper"

    let { product, userInputs = $bindable() }: { product: Product; userInputs?: CartUserInputs } = $props()
    let quantity = $state(1)
    let inputValid = $state(false)
    let tooltipMessage = $state("")
    if (!userInputs) userInputs = {}

    // Check if product can be ordered (maximum_order_quantity = 0 means not orderable)
    let canBeOrdered = $derived(
        product?.maximum_order_quantity === undefined ||
            product?.maximum_order_quantity === null ||
            product?.maximum_order_quantity !== 0
    )

    // Check if product is already in cart with maximum quantity reached
    let isMaxedOutInCart = $derived.by(() => {
        const maxQty = product?.maximum_order_quantity
        if (!maxQty || maxQty <= 0) return false

        const totalProductQuantityInCart = $cart
            .filter((cartItem) => cartItem.product.id === product.id)
            .reduce((total, cartItem) => total + cartItem.quantity, 0)

        return totalProductQuantityInCart >= maxQty
    })

    $effect(() => {
        let invalid = false
        tooltipMessage = ""
        if (product) {
            const hasEngravingLines = [
                product.user_inputs?.engraving_line_length_1,
                product.user_inputs?.engraving_line_length_2,
                product.user_inputs?.engraving_line_length_3,
            ].some(Boolean)

            const missingEngravingInputs = [
                userInputs?.engraving_line_1,
                userInputs?.engraving_line_2,
                userInputs?.engraving_line_3,
            ].every((line) => !line)

            if (hasEngravingLines && missingEngravingInputs) {
                invalid = true
                tooltipMessage = $_("product.tooltip-fill-engraving")
            }

            const { key_number_range_from: rangeFrom, key_number_range_to: rangeTo } = product.user_inputs || {}
            const keyNumber = userInputs?.key_number

            if (
                rangeFrom !== undefined &&
                rangeTo !== undefined &&
                keyNumber !== -1 &&
                (keyNumber === undefined || keyNumber < rangeFrom || keyNumber > rangeTo)
            ) {
                invalid = true
                tooltipMessage = $_("product.tooltip-fill-key-number")
            }

            if ((product?.lasersheet_configurator || [])?.length > 0) {
                const activeLasersheets = product.lasersheet_configurator.map((s) => s.lasersheet_nr)
                const lSE = userInputs.lasersheet_engraving
                if (lasersheetEngraving && activeLasersheets.length && lSE) {
                    if (!isLasersheetConfigValid($lasersheetEngraving, activeLasersheets)) {
                        invalid = true
                        tooltipMessage = $_("product.tooltip-configure-lasersheet")
                    }
                }
            }

            if (!canBeOrdered) {
                tooltipMessage = $_("product.tooltip-not-orderable")
            }
        }

        inputValid = !invalid
    })
</script>

<div class="buttons emphazised-bg">
    {#if product.inquiry}
        <button
            onclick={() => {
                $overlay = {
                    show: true,
                    type: "inquiry",
                    properties: {},
                }
            }}
            class="button primary"
        >
            {$_("product.make-inquiry")}
        </button>
    {:else if !canBeOrdered}
        <div class="not-orderable-message">
            {$_("product.not-orderable")}
        </div>
    {:else if isMaxedOutInCart}
        <div class="already-in-cart-message">
            {$_("product.already-in-cart")}
        </div>
    {:else}
        {#if !inputValid || !canBeOrdered}
            <div class="error-message" role="alert" aria-live="polite">
                {tooltipMessage}
            </div>
        {/if}
        <QuantityCounter bind:quantity {product} />
        <span style="flex-grow: 1; display: flex;">
            <button
                class="button cart-btn primary"
                data-testid="add-to-cart"
                style="flex-grow: 1;"
                disabled={!inputValid || !canBeOrdered}
                aria-describedby={!inputValid || !canBeOrdered ? "cart-button-error" : undefined}
                onclick={() => {
                    $overlay = {
                        show: true,
                        type: "cart",
                        properties: {},
                    }

                    addItemToCart({
                        quantity,
                        product,
                        user_inputs: JSON.parse(JSON.stringify(userInputs)),
                        price: {
                            base: $login ? product.price_net_dealer : product.price_net_end_customer,
                            unit: getProductPrice(product, !!$login),
                        },
                    })
                }}
                ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M7.308 21.116C6.886 21.116 6.53033 20.9713 6.241 20.682C5.95167 20.3927 5.80733 20.0373 5.808 19.616C5.80867 19.1947 5.953 18.839 6.241 18.549C6.53033 18.2603 6.886 18.116 7.308 18.116C7.73 18.116 8.08533 18.2603 8.374 18.549C8.66267 18.8377 8.80733 19.1933 8.808 19.616C8.80867 20.0387 8.664 20.394 8.374 20.682C8.084 20.97 7.72867 21.1147 7.308 21.116ZM16.692 21.116C16.2707 21.116 15.9153 20.9713 15.626 20.682C15.3367 20.3927 15.192 20.0373 15.192 19.616C15.192 19.1947 15.3367 18.839 15.626 18.549C15.9153 18.2603 16.2707 18.116 16.692 18.116C17.1133 18.116 17.469 18.2603 17.759 18.549C18.0477 18.8383 18.192 19.194 18.192 19.616C18.192 20.0373 18.0477 20.3927 17.759 20.682C17.4697 20.9713 17.114 21.116 16.692 21.116ZM5.881 5.5L8.55 11.116H15.185C15.3003 11.116 15.4027 11.087 15.492 11.029C15.582 10.971 15.659 10.891 15.723 10.789L18.339 6.039C18.4157 5.89767 18.422 5.77267 18.358 5.664C18.2933 5.55467 18.1843 5.5 18.031 5.5H5.881ZM5.392 4.5H18.412C18.846 4.5 19.1727 4.67733 19.392 5.032C19.612 5.386 19.6237 5.751 19.427 6.127L16.569 11.335C16.4243 11.5783 16.2363 11.7693 16.005 11.908C15.7737 12.0467 15.5193 12.116 15.242 12.116H8.1L6.885 14.346C6.78233 14.5 6.779 14.6667 6.875 14.846C6.971 15.0253 7.11533 15.1153 7.308 15.116H18.192V16.116H7.308C6.72467 16.116 6.28933 15.87 6.002 15.378C5.71467 14.886 5.70767 14.392 5.981 13.896L7.485 11.216L3.808 3.5H2V2.5H4.442L5.392 4.5Z"
                        fill="white"
                    ></path>
                </svg>
                &nbsp;
                <p class="desktop">
                    {$_("product.add-to-cart")}
                </p>
                <p class="mobile">{$_("product.cart")}</p>
            </button>
        </span>
    {/if}
</div>

<style lang="less">
    @import "../../../../css/variables.less";
    .buttons {
        display: flex;
        gap: 2.4rem;
        align-items: center;
        margin-bottom: 0.6rem;
        flex-wrap: wrap;
        @media @max-mobile {
            gap: 1.2rem;
        }
        > span {
            display: inline-flex;
        }
        .error-message {
            padding: 8px 12px;
            background-color: #fee;
            border: 1px solid #fcc;
            border-radius: 4px;
            color: #c33;
            font-size: 0.9rem;
            width: 100%;
            flex-basis: 100%;
        }
        .cart-btn {
            display: flex;
            padding: 12px 24px !important;
            justify-content: center;
            align-items: center;
            gap: 12px;
            font-size: 1.2rem;
            line-height: normal;
            height: unset;
            height: fit-content;
            @media @max-mobile {
                gap: 0px;
            }
            p {
                margin: 0px;
                padding: 0px;
            }
            .mobile {
                display: none;
                @media @max-mobile {
                    display: block;
                }
            }
            .desktop {
                display: block;
                @media @max-mobile {
                    display: none;
                }
            }
        }
        .not-orderable-message {
            padding: 12px 24px;
            background-color: var(--grey-100);
            border: 1px solid var(--grey-300);
            border-radius: 4px;
            text-align: center;
            color: var(--grey-400);
            font-size: 1rem;
            width: 100%;
        }
        .already-in-cart-message {
            padding: 12px 24px;
            background-color: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 4px;
            text-align: center;
            color: #856404;
            font-size: 1rem;
            width: 100%;
        }
    }
</style>
